<template>
  <div id="new-hsb-subscription-view">
    <b-row
      v-if="annualPaidProduct && canHavePaidSubscription(accountData)"
      align-h="center"
      class="mb-0 pb-0"
      no-gutters
    >
      <b-card-group deck>
        <b-card
          class="subs-info-card mb-0 sub-card"
          :class="isPaidSelected ? 'selected' : ''"
          body-class="subs-info-card-body"
          data-test-hook="new-hsb-subscription-modal-paid-card"
        >
          <template #header>
            <div data-test-hook="new-hsb-paid-subscription-header" class="plan-header">
              {{ annualPaidProduct.name }}
            </div>
          </template>
          <b-row>
            <b-col>
              <CadenceSelectionTabs
                :annual-paid-product="annualPaidProduct"
                :monthly-paid-product="monthlyPaidProduct"
                :selected-product="activePaidSub"
                @setSelectedProduct="setSelectedProduct"
              />
            </b-col>
          </b-row>
          <b-row class="text-center mt-2">
            <b-col>
              <b-button
                variant="subscription-radio"
                pill
                @click="setSelectedProduct(activePaidSub || annualPaidProduct)"
                :pressed="selectedProduct && isPaidSelected"
                data-test-hook="new-hsb-subscription-modal-select-yearly-button"
              >
                <span class="sub-radio-dot align-middle">
                  <i :class="selectedProduct && isPaidSelected ? 'fa-solid fa-circle' : 'fa-regular fa-circle'" />
                </span>
                {{ (isPaidSelected ? selectedProduct.cost : activePaidSub.cost) | formatCurrency }}
                <span class="text-bottom sub-premium-radio-duration">/{{ durationLabel }}</span>
                <span v-if="activePaidSub && activePaidSub.durationInMonths === 12" class="ml-2 savings-label"
                  >- {{ $t('xSavePercent') }}</span
                >
              </b-button>
            </b-col>
          </b-row>
          <div class="subscriptionCardText">
            <b-row v-if="annualPaidProduct && annualPaidProduct.description">
              <b-col data-test-hook="new-hsb-subscription-modal-premium-monitoring-max-products-text">
                {{ annualPaidProduct.description }}
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col>
                <strong data-test-hook="new-hsb-subscription-modal-included-features-text">{{
                  $t('xAdditionalPremiumFeatures')
                }}</strong>
              </b-col>
            </b-row>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.genStatusDesc>
              <b-col cols="1" class="pr-0 text-center">
                <i
                  class="fa-regular fa-circle-check"
                  data-test-hook="new-hsb-subscription-modal-paid-gen-status-icon"
                />
              </b-col>
              <b-col class="float-left" data-test-hook="new-hsb-subscription-modal-paid-gen-status-text">
                {{ $t('xGeneratorStatus') }}
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="genStatusDesc">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-paid-gen-status-desc">
                  {{ $t('xGenStatusDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.manualsDesc>
              <b-col cols="1" class="pr-0 text-center">
                <i class="fa-regular fa-book" data-test-hook="new-hsb-subscription-modal-paid-manuals-icon" />
              </b-col>
              <b-col class="float-left" data-test-hook="new-hsb-subscription-modal-paid-manuals-text">
                {{ $t('xManuals') }}
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="manualsDesc">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-paid-manuals-desc">
                  {{ $t('xManualsDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.statusHistoryDesc>
              <b-col cols="1" class="pr-0 text-center">
                <i
                  class="fa-regular fa-clock-rotate-left"
                  data-test-hook="new-hsb-subscription-modal-paid-status-history-icon"
                />
              </b-col>
              <b-col class="float-left" data-test-hook="new-hsb-subscription-modal-paid-status-history-text">
                {{ $t('xStatusHistory') }}
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="statusHistoryDesc">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-paid-status-history-desc">
                  {{ $t('xStatusHistoryDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.monthlySummaryEmailDesc>
              <b-col cols="1" class="pr-0 text-center">
                <i
                  class="fa-regular fa-envelope-open-text"
                  data-test-hook="new-hsb-subscription-modal-paid-monthly-email-icon"
                />
              </b-col>
              <b-col class="float-left" data-test-hook="new-hsb-subscription-modal-paid-monthly-email-text">
                {{ $t('xMonthlyEmailSummary') }}
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="monthlySummaryEmailDesc">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-paid-monthly-email-desc">
                  {{ $t('xMonthlySummaryEmailDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.maintenanceDetailsDesc>
              <b-col cols="1" class="pr-0 text-center">
                <i class="fa-regular fa-toolbox" data-test-hook="new-hsb-subscription-modal-paid-maintenance-icon" />
              </b-col>
              <b-col class="float-left" data-test-hook="new-hsb-subscription-modal-paid-maintenance-text">
                {{ $t('xMaintenanceTracking') }}
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="maintenanceDetailsDesc">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-paid-maintenance-desc">
                  {{ $t('xMaintenanceTrackingDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.exerciseOnDemandDesc>
              <b-col cols="1" class="pr-0 text-center">
                <i
                  class="colorOrange fa-regular fa-bolt"
                  data-test-hook="new-hsb-subscription-modal-paid-demand-exercise-icon"
                />
              </b-col>
              <b-col class="float-left">
                <strong data-test-hook="new-hsb-subscription-modal-paid-demand-exercise-text">{{
                  $t('xExerciseOnDemand')
                }}</strong>
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="exerciseOnDemandDesc">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-paid-demand-exercise-desc">
                  {{ $t('xExerciseOnDemandDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.realTimeAlertsDesc>
              <b-col cols="1" class="pr-0 text-center">
                <i
                  class="colorOrange fa-regular fa-bell"
                  data-test-hook="new-hsb-subscription-modal-paid-alerts-icon"
                />
              </b-col>
              <b-col class="float-left">
                <strong data-test-hook="new-hsb-subscription-modal-paid-alerts-text"
                  >{{ $t('xRealTimeAlerts') }}*</strong
                >
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="realTimeAlertsDesc">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-paid-alerts-desc">
                  {{ $t('xRealTimeAlertsDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.exerciseTimeDesc>
              <b-col cols="1" class="pr-0 text-center">
                <i
                  class="colorOrange fa-regular fa-clock"
                  data-test-hook="new-hsb-subscription-modal-paid-exercise-time-icon"
                />
              </b-col>
              <b-col class="float-left">
                <strong data-test-hook="new-hsb-subscription-modal-paid-exercise-time-text">{{
                  $t('xExerciseSettings')
                }}</strong>
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="exerciseTimeDesc">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-paid-exercise-time-desc">
                  {{ $t('xExerciseTimeDesc') }}
                </b-col>
              </b-row>
            </b-collapse>

            <b-row align-v="center" class="expandable-click-area" v-b-toggle.dismissWarningsDesc>
              <b-col cols="1" class="pr-0 text-center">
                <i
                  class="colorOrange fa-regular fa-warning"
                  data-test-hook="new-hsb-subscription-modal-dismiss-warnings-icon"
                />
              </b-col>
              <b-col class="float-left">
                <strong data-test-hook="new-hsb-subscription-modal-dismiss-warnings-text">{{
                  $t('xDismissWarnings')
                }}</strong>
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="dismissWarningsDesc">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-dismiss-warnings-desc">
                  {{ $t('xDismissWarningsDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
          </div>
        </b-card>
        <b-card
          v-if="freeProduct"
          class="subs-info-card sub-card"
          :class="!isPaidSelected ? 'selected' : ''"
          body-class="subs-info-card-body"
          data-test-hook="new-hsb-subscription-modal-free-card"
        >
          <template #header>
            <div data-test-hook="new-hsb-free-subscription-header" class="plan-header">
              {{ $t('xMobileLinkBasic') }}
            </div>
          </template>
          <b-row class="text-center mt-2 included-button">
            <b-col>
              <b-button
                variant="subscription-radio"
                pill
                @click="setSelectedProduct(freeProduct)"
                :pressed="selectedProduct && selectedProduct.productId == freeProduct.productId"
                data-test-hook="new-hsb-subscription-modal-free-select-button"
              >
                <span class="sub-radio-dot align-middle">
                  <i
                    :class="
                      selectedProduct && selectedProduct.productId == freeProduct.productId
                        ? 'fa-solid fa-circle'
                        : 'fa-regular fa-circle'
                    "
                  />
                </span>
                <span class="sub-free-radio-text align-middle"> {{ $t('xIncluded') }}</span>
              </b-button>
            </b-col>
          </b-row>
          <div class="subscriptionCardText">
            <b-row class="mb-2">
              <b-col data-test-hook="new-hsb-subscription-modal-basic-monitoring-max-products-text">
                {{ $t('xBasicDescription') }}
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <strong data-test-hook="new-hsb-subscription-modal-free-included-features-text">{{
                  $t('xIncludedBasicFeatures')
                }}</strong>
              </b-col>
            </b-row>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.genStatusDescBasic>
              <b-col cols="1" class="pr-0 text-center">
                <i
                  class="fa-regular fa-circle-check"
                  data-test-hook="new-hsb-subscription-modal-basic-gen-status-icon"
                />
              </b-col>
              <b-col class="float-left" data-test-hook="new-hsb-subscription-modal-basic-gen-status-text">
                {{ $t('xGeneratorStatus') }}
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="genStatusDescBasic">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-basic-gen-status-desc">
                  {{ $t('xGenStatusDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.manualsDescBasic>
              <b-col cols="1" class="pr-0 text-center">
                <i class="fa-regular fa-book" data-test-hook="new-hsb-subscription-modal-basic-manuals-icon" />
              </b-col>
              <b-col class="float-left" data-test-hook="new-hsb-subscription-modal-basic-manuals-text">
                {{ $t('xManuals') }}
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="manualsDescBasic">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-basic-manuals-desc">
                  {{ $t('xManualsDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.statusHistoryDescBasic>
              <b-col cols="1" class="pr-0 text-center">
                <i
                  class="fa-regular fa-clock-rotate-left"
                  data-test-hook="new-hsb-subscription-modal-basic-status-history-icon"
                />
              </b-col>
              <b-col class="float-left" data-test-hook="new-hsb-subscription-modal-basic-status-history-text">
                {{ $t('xStatusHistory') }}
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="statusHistoryDescBasic">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-basic-status-history-desc">
                  {{ $t('xStatusHistoryDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.monthlySummaryEmailDescBasic>
              <b-col cols="1" class="pr-0 text-center">
                <i
                  class="fa-regular fa-envelope-open-text"
                  data-test-hook="new-hsb-subscription-modal-basic-monthly-email-icon"
                />
              </b-col>
              <b-col class="float-left" data-test-hook="new-hsb-subscription-modal-basic-monthly-email-text">
                {{ $t('xMonthlySummaryEmail') }}
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="monthlySummaryEmailDescBasic">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-basic-monthly-email-desc">
                  {{ $t('xMonthlySummaryEmailDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
            <b-row align-v="center" class="expandable-click-area" v-b-toggle.maintenanceDetailsDescBasic>
              <b-col cols="1" class="pr-0 text-center">
                <i class="fa-regular fa-toolbox" data-test-hook="new-hsb-subscription-modal-basic-maintenance-icon" />
              </b-col>
              <b-col class="float-left" data-test-hook="new-hsb-subscription-modal-basic-maintenance-text">
                {{ $t('xMaintenanceTracking') }}
              </b-col>
              <b-col cols="auto" class="float-right when-closed">
                <i class="fa-solid fa-angle-down" />
              </b-col>
              <b-col cols="auto" class="float-right when-open">
                <i class="fa-solid fa-angle-up" />
              </b-col>
            </b-row>
            <b-collapse id="maintenanceDetailsDescBasic">
              <b-row>
                <b-col data-test-hook="new-hsb-subscription-modal-basic-maintenance-desc">
                  {{ $t('xMaintenanceTrackingDesc') }}
                </b-col>
              </b-row>
            </b-collapse>
          </div>
        </b-card>
      </b-card-group>
    </b-row>
    <b-row no-gutters class="mt-3">
      <b-col cols="12" class="float-left pl-2">
        <b-button
          variant="warning"
          class="w-100"
          :disabled="!selectedProduct || isCreatingPurchaseIntent"
          @click="$emit('openPayment')"
          data-test-hook="new-hsb-subscription-modal-confirm-plan-button"
        >
          {{ $t('xContinue') }}
          <b-spinner class="ml-2" v-if="isCreatingPurchaseIntent" />
        </b-button>
      </b-col>
    </b-row>
    <div v-if="showSelectSubError" class="alert alert--validation alert-danger" :show="showSelectSubError">
      {{ $t('xFailedSetUpGeneratorSubscription') }}
    </div>
  </div>
</template>
<script>
import CadenceSelectionTabs from './Common/CadenceSelectionTabs.vue'

export default {
  props: [
    'apparatusId',
    'apparatusDetails',
    'annualPaidProduct',
    'monthlyPaidProduct',
    'accountData',
    'selectedProduct',
    'isCreatingPurchaseIntent'
  ],
  emits: ['openPayment', 'setSelectedProduct'],
  components: {
    CadenceSelectionTabs
  },
  data() {
    return {
      freeProduct: {
        productId: 'free',
        cost: 0
      },
      showSelectSubError: false,
      activePaidSub: {} // the most recent selected cadence - for tracking what the user should see on the paid card when basic is selected
    }
  },
  mounted() {
    if (this.selectedProduct) {
      this.setSelectedProduct(this.selectedProduct)
    }
  },
  methods: {
    setSelectedProduct(product) {
      this.$emit('setSelectedProduct', product)
      if (
        product?.productId === this.annualPaidProduct?.productId ||
        product?.productId === this.monthlyPaidProduct?.productId
      ) {
        this.activePaidSub = product
      }
    }
  },
  computed: {
    isPaidSelected() {
      return (
        this.selectedProduct?.productId === this.annualPaidProduct?.productId ||
        this.selectedProduct?.productId === this.monthlyPaidProduct?.productId
      )
    },
    durationLabel() {
      if (this.activePaidSub?.productId === this.monthlyPaidProduct?.productId) {
        return this.$t('xMonth')
      }

      return this.$t('xYear')
    }
  }
}
</script>
<style lang="scss">
#new-hsb-subscription-view {
  .subs-info-card-body {
    padding: 10px 30px 15px !important;
  }

  h1 {
    font-weight: normal;
    line-height: 1.15;
  }

  .sub-radio-dot {
    font-size: 0.875rem !important;
  }

  .included-button {
    margin-top: 75px !important; // make free button level with paid because of cadence tabs
    // I don't like doing it this way either but the DOM really doesn't wanna give me any offsetHeights
  }

  .subs-info-card {
    border: 2px solid var(--carbon-300) !important;
    padding: 0;
    border-radius: 8px;

    .savings-label {
      color: var(--blue-700);
    }

    .plan-header {
      background-color: var(--carbon-300);
    }

    &.selected {
      border: 2px solid var(--brand-400) !important;

      .plan-header {
        background-color: var(--brand-400);
      }
    }
  }

  .plan-header {
    text-align: center;
    padding: 5px 0px;
    font-size: 24px;
    line-height: 1.35;
    font-weight: 500;
  }

  .card-header {
    align-items: stretch;
    padding: 0;
    border-bottom: 0;
    height: 40px;
  }

  .card-title {
    margin-bottom: 0;
  }

  .btn-subscription-radio {
    color: var(--carbon-900);
    font-size: 20px;
    font-weight: bold;
    padding: 0.375rem 1.5rem;
    border-color: var(--brand-600);
    border-width: 2px;
    margin-bottom: 10px;
    width: 100%;
    text-align: start;
  }

  .btn-subscription-radio:not(:disabled):not(.disabled):hover {
    background-color: var(--orange-200);
  }

  .btn-subscription-radio:not(:disabled):not(.disabled):focus:active {
    box-shadow: 0px 0px 8px rgba(255, 107, 0, 0.25) !important;
    border-width: 2px;
  }

  .btn-subscription-radio.active {
    box-shadow: 0 0 0 0.2rem rgba(255, 107, 0, 0.25) !important;
  }

  .sub-free-radio-text {
    font-size: 16px !important;
  }

  .sub-premium-radio-duration {
    font-size: 16px !important;
    text-transform: lowercase !important;
  }

  .expandable-click-area {
    height: 40px !important;
  }

  .limited-features-header {
    height: 40px;
    background-color: var(--carbon-200);
    display: flex;
    justify-content: center;
    font-weight: bold;
    align-items: center;
  }

  .limited-features {
    background-color: var(--carbon-200);
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 41px;
    font-weight: bold;
  }
}
</style>
